import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  TextField,
  Tooltip,
  TableSortLabel,
  Typography, LinearProgress,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useNavigate } from 'react-router-dom';
import { formatDateTimeToDateTimeBR, formatDateToDateBR, formatDateToDateBRPolish } from '../../utils/date_utils';
import { maturityLevels, typeLabels, statusControl } from '../../utils/labels_utils';


export const ExpandableTable = ({
                           rows,
                           columns,
                                  link,
                                  linkExtend,
                           entityColumns,
                           enableSearch = false,
                           enableSorting = false,
                           title = "Tabela",
                                  onClickShow,
                                  onClickDelete
                         }) => {
  const navigate = useNavigate()
  const [expandedRows, setExpandedRows] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState(null);
  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const newState = { ...prev };
      if (newState[id]) {
        delete newState[id];
      } else {
        newState[id] = true;
      }
      return newState;
    });
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 70) return "#4CAF50";
    if (percentage >= 40) return "#FFC107";
    return "#D32F2F";
  };

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
  };

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    return rows.filter((row) =>
      Object.values(row).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [rows, searchTerm]);

  const displayData = (item, row) => {
    if (row.type === 'boolean') {
      return (
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ 
            width: '12px', 
            height: '12px', 
            borderRadius: '50%', 
            backgroundColor: item[row.id] ? '#22c55e' : '#ef4444',
            display: 'inline-block'
          }}></span>
          {item[row.id] ? 'Sim' : 'Não'}
        </span>
      )
    }
    if (row.type === 'actions-add') {
      return (
        <Table
          sx={{
            padding: '0px',
          }}
        >
          <TableBody>
            <TableRow
              sx={{
                '& td': {
                  border: '0px',
                  padding: '0px',
                },
              }}
            >
              <TableCell
                sx={{
                  textAlign: 'center',
                  '& svg': {
                    fill: '#0039C5',
                    '&:nth-of-type(2)': {
                      marginLeft: '4px',
                    },
                    '&:nth-of-type(3)': {
                      marginLeft: '4px',
                      marginRight: '4px',
                    },
                  },
                  '& svg:hover': {
                    cursor: 'pointer',
                  },
                }}
              >
                {(<NoteAddIcon
                  title="Adicionar Vinculo"
                  onClick={() => {
                    if (onClickShow) {
                      onClickShow(item)
                    } else {
                      navigate(`/${link}/vincular/${item?._id?.toString()}`)
                    }
                  }}
                />)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )
    }
    if (row.type === 'actions-view') {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <VisibilityIcon
            titleAccess="Visualizar"
            sx={{ color: '#0039C5', cursor: 'pointer' }}
            onClick={() => {
              if (onClickShow) {
                onClickShow(item);
              } else {
                navigate(`/${linkExtend}/show/${item?._id?.toString()}`);
              }
            }}
          />
        </Box>
      );
    }
    if (row.type === 'actions') {
      return (
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
          <VisibilityIcon
            titleAccess="Visualizar"
            sx={{ color: '#0039C5', cursor: 'pointer' }}
            onClick={() => {
              if (onClickShow) {
                onClickShow(item);
              } else {
                navigate(`/${linkExtend}/show/${item?._id?.toString()}`);
              }
            }}
          />
          <DeleteOutlineOutlinedIcon
            titleAccess="Remover"
            sx={{ color: '#0039C5', cursor: 'pointer' }}
            onClick={() => onClickDelete(item)}
          />
        </Box>
      );
    }

    if (row.type === 'object') {
      const fields = row.id.split('.')
      return item[fields[0]][fields[1]]
    }
    if (row.type === 'date') {
      return formatDateToDateBRPolish(item[row.id])
    }
    if (row.type === 'datebr') {
      return formatDateToDateBR(item[row.id])
    }
    if (row.type === 'datetime') {
      return formatDateTimeToDateTimeBR(item[row.id], true)
    }
    if (row.type === 'time-expires') {
      if (item[row.id] === -1) {
        return 'Session';
      } else if (item[row.id] < 24) {
        return `${item[row.id]} Hora(s)`
      } else {
        const days = item[row.id] / 24;
        return `${days.toFixed(0)} Dias`
      }
    }
    if (row.type === 'array-object') {
      const fields = row.id.split('.')
      const content = item[fields[0]].findLast(field => field[fields[1]])
      return content ? content[fields[1]] : ''
    }
    if (row.type === 'array-object-datetime') {
      const fields = row.id.split('.')
      const content = item[fields[0]].findLast(field => field[fields[1]])
      return formatDateTimeToDateTimeBR(content?.[fields[1]], true)
    }
    if(row.type === 'text' && item[row.id] === 'null') {
      return ""
    }
    if(row.type === 'type-entity') {
      return typeLabels[item[row.id]] || item[row.id];
    }
    if(row.type === 'status') {
      return statusControl[item[row.id]].label || item[row.id];
    }
    if(row.type === 'maturity-level') {
      return maturityLevels[item[row.id]] || item[row.id];
    }
    if (row.type === "progress") {
      const percentage = item[row.id];
      return (
        <Box display="flex" alignItems="center" gap={1} width="100%">
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height: 6,
              borderRadius: 3,
              flexGrow: 1,
              backgroundColor: "#EEE",
              "& .MuiLinearProgress-bar": {
                backgroundColor: getProgressColor(percentage),
              },
            }}
          />
          <Typography variant="body2" fontWeight="bold" color={getProgressColor(percentage)}>
            {percentage}%
          </Typography>
        </Box>
      );
    }
    if (row.type === "name-description") {
      const nameKey = row.nameKey || "name";
      const descriptionKey = row.descriptionKey || "description";
      const description = item[descriptionKey] || "Sem descrição";

      return (
        <Box>
          <Typography variant="body1" fontWeight="bold">
            {item[nameKey]}
          </Typography>

          <Tooltip title={description} arrow>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
                maxWidth: 300,
              }}
            >
              {description}
            </Typography>
          </Tooltip>
        </Box>
      );
    }


    return item[row.id]
  }

  const sortedRows = useMemo(() => {
    if (!orderBy) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const valA = a[orderBy];
      const valB = b[orderBy];

      if (typeof valA === "string" && typeof valB === "string") {
        return order === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      if (typeof valA === "number" && typeof valB === "number") {
        return order === "asc" ? valA - valB : valB - valA;
      }

      if (typeof valA === "boolean" && typeof valB === "boolean") {
        return order === "asc" ? Number(valA) - Number(valB) : Number(valB) - Number(valA);
      }

      return order === "asc" ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
    });
  }, [filteredRows, orderBy, order]);

  return (
    <TableContainer component={Paper}>
      {/* Barra de busca */}
      {enableSearch && (
        <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <Typography variant="h6" color="primary">{title}</Typography>
          <TextField
            label="Pesquisar"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              borderRadius: "20px",
              "& .MuiOutlinedInput-root": { borderRadius: "20px" },
            }}
          />
        </Box>
      )}

      <Table>
        <TableHead>
          <TableRow>

            {columns.map((column) => (
              <TableCell key={column.id}>
                {enableSorting && column.sortable ? (
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRows.map((row) => (
            <React.Fragment key={row._id}>
              <TableRow>
                {columns.map((column, colIndex) => (
                  <TableCell key={column.id}>
                    {displayData(row, column, colIndex)}
                  </TableCell>
                ))}
                <TableCell>
                  {row.entities?.length > 0 && (
                    <IconButton size="small" onClick={() => toggleRow(row._id)}>
                      {expandedRows[row._id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>

              {/* Linha oculta com os vínculos (entities) */}
              {expandedRows[row._id] && row.entities?.length > 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} style={{ padding: 0 }}>
                    <Collapse in={expandedRows[row._id]} timeout="auto" unmountOnExit>
                      <Box margin={1}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              {entityColumns.map((entityColumn) => (
                                <TableCell key={entityColumn.id}>{entityColumn.label}</TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {row.entities.map((entity, entityIndex) => (
                              <TableRow key={entity._id}>
                                {entityColumns.map((entityColumn) => (
                                  <TableCell key={entityColumn.id}>
                                    {displayData(entity, entityColumn, entityIndex)}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>

      </Table>
    </TableContainer>
  );
};

export default ExpandableTable;
